/**
 * FestivalBanner — shows the workspace's currently-active festival theme (if
 * imported by admin) as a slim top strip. Also applies a subtle accent color
 * override for the day.
 */
import { useEffect, useState } from "react";
import { useAuth } from "@/lib/auth";
import api from "@/lib/api";

export default function FestivalBanner() {
  const { activeWorkspaceId } = useAuth();
  const [theme, setTheme] = useState(null);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    if (!activeWorkspaceId) { setTheme(null); return; }
    let cancelled = false;
    const fetchActive = async () => {
      try {
        const { data } = await api.get(`/workspaces/${activeWorkspaceId}/festival-themes/active`);
        if (!cancelled) {
          setTheme(data?.active || null);
          setDismissed(false); // re-show on refresh (e.g. after a manual apply)
        }
      } catch { if (!cancelled) setTheme(null); }
    };
    fetchActive();
    const onRefresh = () => fetchActive();
    window.addEventListener("festival:refresh", onRefresh);
    return () => {
      cancelled = true;
      window.removeEventListener("festival:refresh", onRefresh);
    };
  }, [activeWorkspaceId]);

  useEffect(() => {
    if (!theme) return;
    const key = `acquilix.festival.dismiss.${theme.id}.${new Date().toISOString().slice(0, 10)}`;
    if (localStorage.getItem(key) === "1") setDismissed(true);
  }, [theme]);

  if (!theme || dismissed) return null;
  return (
    <div
      style={{
        background: theme.banner_gradient || "#111",
        color: theme.banner_text_color || "#fff",
      }}
      className="border-b border-black/10"
      data-testid={`festival-banner-${theme.id}`}
    >
      <div className="max-w-full px-6 py-2 flex items-center gap-2 text-sm">
        <span className="text-lg">{theme.emoji || "🎉"}</span>
        <span className="font-semibold">{theme.banner_message || theme.name}</span>
        <button
          onClick={() => {
            const key = `acquilix.festival.dismiss.${theme.id}.${new Date().toISOString().slice(0, 10)}`;
            localStorage.setItem(key, "1");
            setDismissed(true);
          }}
          className="ml-auto text-xs opacity-70 hover:opacity-100 underline"
          data-testid="festival-dismiss"
        >
          Dismiss
        </button>
      </div>
    </div>
  );
}
